import { useAnalytics } from "@accurx/analytics";
import * as UI from "@accurx/design";
import { Ds } from "@accurx/design";
import { Feedback } from "@accurx/inbox-design-library";
import { PatientExternalId } from "@accurx/native";
import { useCompose } from "domains/message-component/context";
import { ContactDetail } from "domains/message-component/reducer.types";

import { useCommunicationConsent } from "./CommunicationConsent/ConsentAggregate/useCommunicationConsent";
import { PatientCommunicationConsentSummary } from "./CommunicationConsent/PatientCommunicationConsentSummary";
import { ContactButton } from "./ContactButton";
import { NewContactOptionButton } from "./NewContactOptionButton";
import {
    StyledContactDetailsList,
    StyledDivider,
} from "./RecipientSelector.styles";

type ContactDetailsListContentProps = {
    contactDetails: ContactDetail[];
    patientExternalIds: PatientExternalId[];
    setIsOpen: (open: boolean) => void;
    setShowCustomContactDetailsForm: (showForm: boolean) => void;
    shouldDisableEmail: boolean;
};

export const ContactDetailsListContent = ({
    contactDetails,
    patientExternalIds,
    setIsOpen,
    setShowCustomContactDetailsForm,
    shouldDisableEmail,
}: ContactDetailsListContentProps) => {
    const { dispatch } = useCompose();

    const track = useAnalytics();

    const { size: viewPort } = UI.Hooks.useViewportSize();
    const isMobileSize = viewPort === "xs";

    const communicationConsentQuery = useCommunicationConsent({
        patientExternalIds,
    });

    return (
        <>
            {isMobileSize && (
                <Ds.Popover.Header>
                    <Ds.Text
                        as="h2"
                        size="large"
                        id="custom-contact-detail-form"
                        weight="bold"
                    >
                        Select contact method
                    </Ds.Text>
                </Ds.Popover.Header>
            )}
            <Ds.Popover.Body p="1">
                <UI.VisuallyHidden>
                    <Ds.Text id="recipient-selector">
                        Select contact method
                    </Ds.Text>
                </UI.VisuallyHidden>
                <Ds.Flex flexDirection="column" gap="0.25">
                    {communicationConsentQuery?.status === "error" && (
                        <Feedback
                            title="An error occurred when loading patient communication consent"
                            colour="warning"
                        >
                            <Ds.Text as="span" color="night" size="small">
                                Check the medical record for details
                            </Ds.Text>
                        </Feedback>
                    )}
                    <StyledContactDetailsList>
                        {contactDetails.map((contactOption, i) => (
                            <li
                                key={`${contactOption.origin}-${contactOption.method}-${i}`}
                                data-testid="contact-option"
                            >
                                <ContactButton
                                    disabled={
                                        shouldDisableEmail &&
                                        contactOption.method === "Email"
                                    }
                                    patientExternalIds={patientExternalIds}
                                    contactDetail={contactOption}
                                    onClick={() => {
                                        dispatch({
                                            type: "UPDATE_CONTACT_DETAILS",
                                            payload: {
                                                contactDetails: contactOption,
                                            },
                                        });
                                        track(
                                            "PatientMessageContactOptionConfirm Button Click",
                                            {
                                                contactType:
                                                    contactOption.method,
                                                contactOrigin:
                                                    contactOption.origin,
                                            },
                                        );
                                        setIsOpen(false);
                                    }}
                                />
                            </li>
                        ))}
                    </StyledContactDetailsList>

                    <PatientCommunicationConsentSummary
                        patientExternalIds={patientExternalIds}
                    />
                    <StyledDivider />
                    <NewContactOptionButton
                        onClick={() => setShowCustomContactDetailsForm(true)}
                    />
                </Ds.Flex>
            </Ds.Popover.Body>
        </>
    );
};
